import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Heading, Toast } from '@vibe/core'
import { AuthGuard } from '../components/common/AuthGuard'
import { Navigation } from '../components/layout/Navigation'
import { DynamicRequirementForm } from '../components/forms/DynamicRequirementForm'
import { apiService } from '../services/api'
import { getErrorMessage } from '../utils/errorHandling'
import type { JobDefinitionSelectorOption, RequirementFormData } from '../types'

const NewRequirement: React.FC = () => {
  const navigate = useNavigate()
  const [selectedJobDefinitionId, setSelectedJobDefinitionId] =
    useState<string>('')
  const [selectedJobDefinition, setSelectedJobDefinition] = useState<
    JobDefinitionSelectorOption | undefined
  >()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string>('')
  const [toast, setToast] = useState<{
    message: string
    type: 'positive' | 'negative'
  } | null>(null)

  // Handle job definition selection
  const handleJobDefinitionChange = useCallback(
    (jobDefinitionId: string, jobDefinition?: JobDefinitionSelectorOption) => {
      setSelectedJobDefinitionId(jobDefinitionId)
      setSelectedJobDefinition(jobDefinition)
      // Clear error when user makes a selection
      if (jobDefinitionId && error) {
        setError('')
      }
    },
    [error]
  )

  // Handle form submission from DynamicRequirementForm
  const handleFormSubmit = useCallback(
    async (formData: RequirementFormData) => {
      // Validate job definition selection
      if (!selectedJobDefinitionId) {
        setError('Debe seleccionar una definición de puesto')
        return
      }

      setIsSubmitting(true)
      setError('')

      try {
        // Create requirement with job definition and form data
        const response = await apiService.createRequirement({
          job_definition_id: selectedJobDefinitionId,
          ...formData, // Include all form fields (including puesto_solicitado)
        })

        console.log('Requirement created successfully:', response)

        // Show success message
        setToast({
          message: response.data.message || 'Requerimiento creado exitosamente',
          type: 'positive',
        })

        // Navigate to dashboard after success
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
      } catch (error) {
        console.error('Error creating requirement:', error)

        let errorMessage = getErrorMessage(error)

        // Handle specific API error cases
        if (error instanceof Error) {
          if (error.message.includes('job_definition_id')) {
            errorMessage = 'La definición de puesto seleccionada no es válida'
          } else if (error.message.includes('not found')) {
            errorMessage =
              'La definición de puesto no existe o no está disponible'
          } else if (
            error.message.includes('network') ||
            error.message.includes('fetch')
          ) {
            errorMessage = 'Error de conexión. Por favor intenta nuevamente.'
          }
        }

        setToast({
          message: errorMessage,
          type: 'negative',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [selectedJobDefinitionId, navigate]
  )

  // Handle form cancellation
  const handleCancel = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  // Memoize initial data to prevent unnecessary re-renders
  const initialFormData = useMemo(
    () => ({
      puesto_solicitado: selectedJobDefinition?.puesto || '',
    }),
    [selectedJobDefinition?.puesto]
  )

  // Memoize job definition selector props
  const jobDefinitionSelectorProps = useMemo(
    () => ({
      value: selectedJobDefinitionId,
      onChange: handleJobDefinitionChange,
      error: error,
    }),
    [selectedJobDefinitionId, handleJobDefinitionChange, error]
  )

  // Auto-hide toast
  React.useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

        <main className="container mx-auto px-4 py-8">
          <Box className="mb-8">
            <Heading type="h1" className="mb-2">
              Crear Requerimiento
            </Heading>
          </Box>

          {/* Toast notifications */}
          {toast && (
            <Toast
              open={true}
              onClose={() => setToast(null)}
              className="fixed top-4 right-4 z-50"
              type={toast.type}
            >
              {toast.message}
            </Toast>
          )}

          {/* Single unified form */}
          <DynamicRequirementForm
            onSubmit={handleFormSubmit}
            onCancel={handleCancel}
            initialData={initialFormData}
            isSubmitting={isSubmitting}
            jobDefinitionSelector={jobDefinitionSelectorProps}
          />
        </main>
      </div>
    </AuthGuard>
  )
}

export default NewRequirement
